import { sortBy, find, debounce, assign } from "lodash";
import { useRef, useEffect, useMemo, useState } from "react";
import useLocalStorage from "@rehooks/local-storage";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { ACTIONS, EVENTS, LIFECYCLE } from "react-joyride";
import dayjs from "lib/dayjs";
import { useUserData } from "features/User";
import tours from "../data/sooth-tours.data";

export default function useTour({ debug = false }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const { user, groupId, isSubscriptionLoading } = useUserData();

  const tour = useRef(null);
  const activeTarget = useRef(null);

  const [key, setKey] = useState(dayjs().toISOString());
  const [beacon, setBeacon] = useState(true);
  const [pause, setPause] = useState(false);

  const [toursSeen, setToursSeen] = useLocalStorage(
    `user-tours:tours-seen:${user.userId}`,
    []
  );

  const [lastTourComplete, setLastTourComplete] = useLocalStorage(
    `user-tours:last-tour-complete:${user.userId}`,
    null
  );

  const [stepIndex, setStepIndex] = useLocalStorage(
    `user-tours:step-index:${user.userId}`,
    -1
  );

  const [currentTour, setCurrentTour] = useLocalStorage(
    `user-tours:current-tour:${user.userId}`,
    null
  );

  const timeSinceLastTour = useMemo(() => {
    return dayjs
      .utc()
      .diff(
        lastTourComplete != null ? lastTourComplete : dayjs.utc().toISOString(),
        process.env.REACT_APP_TOURS_FORCED_DELAY_INCREMENT
      );
  }, [lastTourComplete]);

  const activeTour = useMemo(() => {
    const tour = find(tours, (t) => t.id === currentTour);
    return tour;
  }, [currentTour]);

  const steps = useMemo(() => {
    if (activeTour == null) {
      return null;
    }
    return setupTourSteps(activeTour.steps, !!activeTour.force);
  }, [activeTour]);

  const runTour = useMemo(() => {
    if (steps == null) {
      return false;
    }
    if (
      lastTourComplete != null &&
      timeSinceLastTour <
        Math.ceil(process.env.REACT_APP_TOURS_FORCED_DELAY * process.env.REACT_APP_TOURS_SOFT_DELAY)
    ) {
      return false;
    }
    let triggerTour = true;
    if (typeof activeTour.trigger === "function") {
      triggerTour = activeTour.trigger(location);
    }
    const runTour = !pause && (currentTour != null || triggerTour);
    if (debug) {
      console.log("USETOUR:RUNTOUR", runTour);
    }
    return runTour;
  }, [activeTour, steps, currentTour, debug, pause]);

  function setupTourSteps(steps, force = false) {
    return steps.map((step, index) => {
      if (index !== 0) {
        return step;
      }
      return assign(
        { ...step },
        {
          disableBeacon:
            timeSinceLastTour >
              Number(process.env.REACT_APP_TOURS_FORCED_DELAY) || force,
        }
      );
    });
  }

  function resetAll() {
    setLastTourComplete(null);
    setStepIndex(-1);
    setCurrentTour(null);
    setToursSeen([]);
  }

  function nextTourStep(index) {
    const nextStep =
      index != null && index >= 0
        ? index + 1
        : stepIndex >= 0
        ? stepIndex + 1
        : 0;
    setStepIndex(nextStep);
  }

  function previousTourStep(index) {
    const prevStep =
      index != null && index > 0
        ? index - 1
        : stepIndex > 0
        ? stepIndex - 1
        : 0;
    setStepIndex(prevStep);
  }

  function tourStart({ detail: { step } }) {
    if (stepIndex > 0) {
      setStepIndex(0);
    }
    if (debug) {
      console.log("TOURSTART");
    }
    if (typeof activeTour?.start === "function") {
      activeTour.start(navigate, step, debug);
    }
  }

  async function tourEnd() {
    if (debug) {
      console.log("TOUREND");
    }
    if (
      activeTour?.cleanup != null &&
      typeof activeTour?.cleanup === "function"
    ) {
      await activeTour.cleanup(navigate, () => {
        setToursSeen([...toursSeen, currentTour]);
        setStepIndex(-1);
        setCurrentTour(null);
        setLastTourComplete(dayjs.utc().toISOString());
      });
    } else {
      setToursSeen([...toursSeen, currentTour]);
      setStepIndex(-1);
      setCurrentTour(null);
      setLastTourComplete(dayjs.utc().toISOString());
    }
    searchParams.delete("tourSearchOpen");
    setSearchParams(searchParams);
  }

  async function stepAfter({ detail: { step, action, index } }) {
    const nextIndex = index + (action === ACTIONS.PREV ? -1 : 1);
    const nextStep = steps[nextIndex];
    if (
      nextStep?.data?.setup != null &&
      typeof nextStep?.data?.setup === "function"
    ) {
      if (debug) {
        console.log("STEPAFTERFUNC", nextStep);
      }
      await nextStep.data.setup({
        tour: tour.current,
        step,
        nextStep,
        index,
        action:
          action === ACTIONS.PREV
            ? "tour:prev"
            : action === ACTIONS.NEXT
            ? "tour:next"
            : null,
        navigate,
        continueTour: () => {
          if (action === ACTIONS.PREV) {
            previousTourStep();
          } else if (action === ACTIONS.NEXT) {
            nextTourStep();
          }
        },
        debug,
      });
    } else {
      if (action === ACTIONS.PREV) {
        previousTourStep();
      } else if (action === ACTIONS.NEXT) {
        nextTourStep();
      }
    }
  }

  let errorAdvanceDelay = null;

  async function handleTour(data) {
    const { action, index, type, step, lifecycle } = data;

    if (debug) {
      console.groupCollapsed(`${type}:step:${index}`);
      console.log(data);
      console.log(tour);
    }

    if (errorAdvanceDelay != null) {
      clearTimeout(errorAdvanceDelay);
      errorAdvanceDelay = null;
    }

    if (lifecycle === LIFECYCLE.BEACON) {
      setBeacon(true);
    } else if (lifecycle === LIFECYCLE.TOOLTIP) {
      setBeacon(false);
    }
    switch (type) {
      case EVENTS.TOUR_START:
        tourStart(
          new CustomEvent(type, {
            detail: {
              step,
            },
          })
        );
        break;
      case EVENTS.TOUR_END:
        tourEnd(
          new CustomEvent(type, {
            detail: {
              step,
            },
          })
        );
        break;
      // case EVENTS.STEP_BEFORE:
      //   activeTarget.current = document.querySelector(step.target);
      //   if (debug) {
      //     console.log(activeTour.current, step.data);
      //   }
      //   const scroll =
      //     typeof step.data?.scrollTo === "boolean" ? step.data?.scrollTo : true;
      //   if (activeTarget.current != null && scroll) {
      //     setTimeout(() => {
      //       activeTarget.current.scrollIntoView({
      //         behavior: "smooth",
      //         block: "center",
      //         // container: "nearest",
      //       });
      //     }, 0);
      //     // const observer = new IntersectionObserver(
      //     //   (entries) => {
      //     //     console.log(entries[0]);
      //     //     if (entries.length > 0 && entries[0].isIntersecting) {
      //     //       // setPause(false);
      //     //       observer.disconnect();
      //     //     } else {
      //     //       // setPause(true);
      //     //     }
      //     //   },
      //     //   {
      //     //     threshold: 1,
      //     //     trackVisibility: true,
      //     //     delay: 250,
      //     //   }
      //     // );
      //     // observer.observe(activeTarget.current);
      //   }
      //   break;
      case EVENTS.STEP_AFTER:
        if (activeTarget.current != null) {
          activeTarget.current = null;
        }
        stepAfter(
          new CustomEvent(type, {
            detail: {
              step,
              action,
              index,
            },
          })
        );
        break;
      case EVENTS.TARGET_NOT_FOUND:
        errorAdvanceDelay = setTimeout(() => {
          if (action === ACTIONS.PREV) {
            previousTourStep();
          } else if (action === ACTIONS.NEXT) {
            nextTourStep();
          }
        }, 1000);
        break;
      case EVENTS.ERROR:
      default:
        break;
    }
    if (debug) {
      console.groupEnd();
    }
  }

  useEffect(() => {
    if (!isSubscriptionLoading && currentTour == null) {
      const nextTour = find(
        sortBy(tours, (t) => t.order),
        (t) =>
          t.active &&
          !toursSeen.includes(t.id) &&
          (typeof t.isValidTour === "function"
            ? t.isValidTour(user, groupId)
            : true)
      );
      if (nextTour != null) {
        setTimeout(() => {
          setCurrentTour(nextTour.id);
          setStepIndex(0);
        }, 250);
      }
    }
    // else if (currentTour != null && stepIndex < 0) {
    //   setStepIndex(0);
    // }
  }, [currentTour, user, groupId, isSubscriptionLoading]);

  useEffect(() => {
    if (beacon) {
      setTimeout(() => setKey(dayjs().toISOString()), 150);
    }
  }, [location, beacon]);

  useEffect(() => {
    const resizeTrigger = debounce(() => {
      setTimeout(() => {
        window.dispatchEvent(new Event("resize"));
      }, 0);
    }, 100);

    function triggerNext({ detail }) {
      if (debug) {
        console.log("TOUR:NEXT:TRIGGER", detail);
      }
      nextTourStep(detail);
    }
    function triggerPrev({ detail }) {
      if (debug) {
        console.log("TOUR:PREVIOUS:TRIGGER", detail);
      }
      previousTourStep(detail);
    }
    function triggerReset(event) {
      if (debug) {
        console.log(
          "TRIGGERRESET",
          event,
          currentTour,
          JSON.stringify(stepIndex)
        );
      }
      if (currentTour != null && stepIndex > 0) {
        setStepIndex(0);
        setTimeout(() => {
          tourStart(
            new CustomEvent("tour:reset", {
              detail: {
                step: steps[0],
              },
            })
          );
        }, 0);
      }
    }
    window.addEventListener("tour:next", triggerNext);
    window.addEventListener("tour:prev", triggerPrev);
    window.addEventListener("scrollend", resizeTrigger);
    window.addEventListener("popstate", triggerReset);
    window.addEventListener("beforeunload", triggerReset);

    //dom observer
    const observer = new MutationObserver(resizeTrigger);

    observer.observe(document.body, {
      attributes: true, // Observe attribute changes
      childList: true, // Observe additions/removals of child nodes
      subtree: true, // Observe changes in descendants as well
      characterData: true, // Observe changes in text content
    });

    return () => {
      window.removeEventListener("tour:next", triggerNext);
      window.removeEventListener("tour:prev", triggerPrev);
      window.removeEventListener("scrollend", resizeTrigger);
      window.removeEventListener("popstate", triggerReset);
      window.removeEventListener("beforeunload", triggerReset);
      observer.disconnect();
    };
  }, [debug]);

  return {
    debug,
    handleTour,
    key,
    resetAll,
    runTour,
    stepIndex,
    steps,
    tour,
  };
}
